import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { generateDocuments } from '../utils/documentGenerator';
import { useSession } from '../hooks/useSession';

const { FiDownload, FiEdit3, FiShare2, FiFileText, FiCheck, FiArrowLeft } = FiIcons;

const DocumentPreview = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { session, loading, error } = useSession(sessionId);
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(0);
  const [isGenerating, setIsGenerating] = useState(true);

  useEffect(() => {
    if (!loading && session) {
      // Generate documents from session data
      const generatedDocs = generateDocuments(session.type, session.answers);
      setDocuments(generatedDocs);
      setIsGenerating(false);
    } else if (!loading && !session && error) {
       // Only navigate away if done loading and no session found
       // navigate('/');
    }
  }, [session, loading, error, navigate]);

  const handleDownload = (doc) => {
    // Create blob and download
    const blob = new Blob([doc.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${doc.title}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadAll = () => {
    documents.forEach(doc => handleDownload(doc));
  };

  if (loading || isGenerating) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-100 to-purple-100 flex items-center justify-center mx-auto mb-6 animate-pulse">
            <SafeIcon icon={FiFileText} className="text-3xl text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Generating Your Documents</h2>
          <p className="text-slate-600 mb-6">AI is crafting your customized agreements...</p>
          <div className="w-64 bg-slate-200 rounded-full h-2 mx-auto">
            <motion.div
              className="progress-bar h-full rounded-full"
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration: 2, ease: "easeOut" }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (!session || documents.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-4">No Documents Available</h2>
          <p className="text-slate-600 mb-6">Unable to generate documents. Please try again.</p>
          <button onClick={() => navigate('/')} className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-10 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => navigate(`/wizard/${session.type}`)} className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors">
              <SafeIcon icon={FiArrowLeft} /> Back to Wizard
            </button>
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(`/wizard/${session.type}`)} className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors">
                <SafeIcon icon={FiEdit3} /> Edit Agreement
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                <SafeIcon icon={FiShare2} /> Share Documents
              </button>
              <button onClick={handleDownloadAll} className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <SafeIcon icon={FiDownload} /> Download All
              </button>
            </div>
          </div>
          <div className="text-center">
            <h1 className="text-4xl font-bold text-slate-800 mb-4">Your Documents Are Ready!</h1>
            <p className="text-xl text-slate-600">
              Review and download your customized legal agreements
            </p>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Document List */}
          <div className="lg:col-span-1">
            <div className="glass-effect rounded-xl p-6 sticky top-24">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Generated Documents</h3>
              <div className="space-y-3">
                {documents.map((doc, index) => (
                  <motion.button
                    key={index}
                    onClick={() => setSelectedDoc(index)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      selectedDoc === index
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-slate-800 mb-1">{doc.title}</h4>
                        <p className="text-sm text-slate-600">{doc.description}</p>
                      </div>
                      <SafeIcon icon={FiFileText} className="text-slate-400 ml-2" />
                    </div>
                  </motion.button>
                ))}
              </div>
              
              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="flex items-center gap-2 text-green-600 mb-2">
                  <SafeIcon icon={FiCheck} />
                  <span className="text-sm font-medium">All Documents Generated</span>
                </div>
                <p className="text-xs text-slate-600">
                  Documents are ready for review and signing
                </p>
              </div>
            </div>
          </div>

          {/* Document Viewer */}
          <div className="lg:col-span-3">
            <motion.div
              key={selectedDoc}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-effect rounded-xl"
            >
              {/* Document Header */}
              <div className="flex items-center justify-between p-6 border-b border-slate-200">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">{documents[selectedDoc]?.title}</h2>
                  <p className="text-slate-600">{documents[selectedDoc]?.description}</p>
                </div>
                <button
                  onClick={() => handleDownload(documents[selectedDoc])}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <SafeIcon icon={FiDownload} /> Download
                </button>
              </div>

              {/* Document Content */}
              <div className="p-6">
                <div className="bg-white rounded-lg border border-slate-200 p-8 font-mono text-sm leading-relaxed max-h-[600px] overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-slate-800">
                    {documents[selectedDoc]?.content}
                  </pre>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Summary Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 glass-effect rounded-xl p-8"
        >
          <h3 className="text-2xl font-bold text-slate-800 mb-6">Agreement Summary</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-semibold text-slate-800 mb-2">Agreement Type</h4>
              <p className="text-slate-600 capitalize">{session.type} Agreement</p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-2">Documents Generated</h4>
              <p className="text-slate-600">{documents.length} documents</p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-800 mb-2">Next Steps</h4>
              <p className="text-slate-600">Review, download, and get signatures</p>
            </div>
          </div>

          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <SafeIcon icon={FiCheck} className="text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-800 mb-1">Legal Disclaimer</h4>
                <p className="text-sm text-blue-700">
                  These documents are generated based on your inputs and common legal practices. We recommend having them reviewed by a qualified attorney before signing, especially for complex agreements or significant financial commitments.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DocumentPreview;